<script setup>
import {ref, computed, defineAsyncComponent, getCurrentInstance, watch} from 'vue'
import { useRoute, useRouter } from 'vue-router'

  import termsOfUseGROK from '@/assets/grok/policy/_grok_terms_of_use.pdf'
  import termsOfUseARVAND from '@/assets/arvand/policy/oferta_2025.pdf'
  import { shortenString } from "@/utils/strings.js"
  import { getCurrencySymbol } from "@/utils/currency.js"
  import QRCodeVue3 from "qrcode-vue3";

  const cashModalTextHTML = `
    <h2>Пополнение счёта наличными в кассе РНКО «СТАТЕРА» (ООО)</h2>

    <p>Для пополнения счёта наличными необходимо лично обратиться в отделение РНКО «СТАТЕРА» (ООО) по адресу:</p>

    <address>
      384900, Республика Абхазия,<br>
      г. Сухум, ул. Лакоба, д. 34
    </address>

    <p>При обращении в кассу необходимо предоставить:</p>

    <ul>
      <li>Паспорт на имя владельца счёта.</li>
      <li>Информацию для оформления заявки:
        <ul>
          <li>Фамилия, имя, отчество владельца счёта;</li>
          <li>Сумма пополнения;</li>
          <li>Номер счёта или иные необходимые реквизиты.</li>
        </ul>
      </li>
    </ul>

    <p>Сотрудник кассы оформит заявку на основании предоставленных данных. После проверки документов будет осуществлено пополнение счёта наличными.</p>

    <p><strong>Важно:</strong> паспортные данные должны совпадать с информацией, указанной в электронном кошельке.</p>
  `

  const BaseButton = defineAsyncComponent(() =>
      import(/* webpackChunkName: "components/base/buttons/BaseButton.vue" */ '@/components/base/buttons/BaseButton.vue')
  )
  const BackBtn = defineAsyncComponent(() =>
      import(/* webpackChunkName: "components/base/buttons/BackBtn.vue" */ '@/components/base/buttons/BackBtn.vue')
  )
  const Copy = defineAsyncComponent(() =>
      import(/* webpackChunkName: "components/base/clipboardCopy/copy.vue" */ '@/components/base/clipboardCopy/copy.vue')
  )
  const TextModal = defineAsyncComponent(() =>
      import(/* webpackChunkName: "components/TextModal.vue" */ '@/components/TextModal.vue')
  )
  const WalletList = defineAsyncComponent(() =>
      import(/* webpackChunkName: "components/WalletList.vue" */ '@/components/WalletList.vue')
  )
  const BaseInput = defineAsyncComponent(() =>
      import(/* webpackChunkName: "BaseInput" */ '@/components/base/inputs/BaseInput.vue')
  )

  import { useAuthStore } from '@/store/stores/auth'
  const authStore = useAuthStore()

  import { useStore } from 'vuex'
  const vuexStore = useStore()

  import { settings } from "../../appType/settings.js";
  const envName = settings.env.VUE_APP_ENV_NAME

  const instance = getCurrentInstance();
  const $http = instance.proxy.$http
  const text = instance.proxy.text
  const route = useRoute()
  const router = useRouter()
  const amount = ref('')
  const errorMessage = ref('')
  const cashModalActive = ref(false)
  const cashModalText = cashModalTextHTML
  const termsAccepted = ref(true)
  const loading = ref(false)
  const paymentUrl = ref('')
  const createdTransaction = ref(null)
  const createdTransactionFallback = ref(null)
  const transactionDetailsLoaded = ref(false)
  const transactionLoading = ref(false)
  const transactionRefreshAnimating = ref(false)
  const topUpLimits = ref(null)
  const topUpLimitsLoading = ref(false)
  const minRefreshAnimationMs = 1000

  const authInfo = computed(() => {
    return vuexStore.state.authInfo || {}
  })
  const activeWallet = computed(() => {
    return vuexStore.state.currentWallet
  })
  const activeCurrency = computed(() => {
    return  activeWallet.value?.currency_code
  })
  const dynamicTermsLink = computed(() => {
    if (envName === 'arvand') {
      return termsOfUseARVAND
    } else if (envName === 'grok') {
      return termsOfUseGROK
    }
    return null
  })

  const termsModel = computed({
    get() {
      return termsAccepted.value = ![null, 'false'].includes(localStorage.getItem('termsAccepted'))
    },
    set(v) {
      localStorage.setItem('termsAccepted', v)
      termsAccepted.value = Boolean(v)
    }
  })

  const authToken = computed(() => {
    return vuexStore.state.authToken
  })
  const walletList = computed(() => {
    return vuexStore.state.walletList || []
  })
  const routeAccountId = computed(() => {
    return route.query.account_id ? String(route.query.account_id) : ''
  })
  const isTopUpAllowed = computed(() => {
    return authInfo.value['is_topup_allowed']
  })
  const topUpDisabled = computed(() => {
    if (dynamicTermsLink.value) {
      return !isTopUpAllowed.value || loading.value || !Number(amount.value) || !termsAccepted.value
    } else {
      return !isTopUpAllowed.value || loading.value || !Number(amount.value)
    }
  })

  const routeWalletTransactionId = computed(() => {
    return route.name === 'WalletTopUpTransaction' && route.params.id
      ? String(route.params.id)
      : ''
  })
  const topUpTransaction = computed(() => {
    if (!createdTransaction.value && !createdTransactionFallback.value && routeWalletTransactionId.value) {
      return {
        id: routeWalletTransactionId.value,
        status: 'pending',
        wallet_transaction_type: 'topup',
      }
    }
    return createdTransaction.value || createdTransactionFallback.value
  })
  const walletTransactionId = computed(() => {
    return topUpTransaction.value?.id || createdTransactionFallback.value?.id
  })
  const isFinalTopUpStatus = computed(() => {
    return ['complete', 'failed'].includes(topUpTransaction.value?.status)
  })
  const canContinueToPayment = computed(() => {
    if (!paymentUrl.value || isFinalTopUpStatus.value) {
      return false
    }

    try {
      const url = new URL(paymentUrl.value, window.location.origin)
      return ['http:', 'https:'].includes(url.protocol)
    } catch (error) {
      return false
    }
  })
  const showRedirectUnavailable = computed(() => {
    return transactionDetailsLoaded.value
      && !transactionLoading.value
      && !canContinueToPayment.value
      && !isFinalTopUpStatus.value
  })
  const visibleTopUpLimitParts = computed(() => {
    if (!topUpLimits.value || topUpLimitsLoading.value) {
      return []
    }

    const limits = topUpLimits.value
    const parts = []
    if (limits.limit_min_amount) {
      parts.push(`min ${formatLimitAmount(limits.limit_min_amount)}`)
    }
    if (limits.limit_max_amount) {
      parts.push(`max ${formatLimitAmount(limits.limit_max_amount)}`)
    }
    if (limits.limit_day_amount) {
      parts.push(`daily ${formatLimitAmount(limits.limit_day_amount)}`)
    }
    if (limits.limit_day_count !== null && limits.limit_day_count !== undefined) {
      parts.push(`${limits.limit_day_count} tx/day`)
    }
    return parts
  })
  const visibleTopUpLimits = computed(() => {
    return visibleTopUpLimitParts.value.join(' · ')
  })

  const formatLimitAmount = (value) => {
    const numberValue = Number(value)
    const formattedValue = Number.isFinite(numberValue)
      ? new Intl.NumberFormat(undefined, {
        maximumFractionDigits: 4,
      }).format(numberValue)
      : value
    return `${formattedValue} ${getCurrencySymbol(activeCurrency.value)}`
  }

  watch(activeWallet, () => {
    amount.value = ''
  })

  const fetchTopUpLimits = async () => {
    if (!authToken.value || !activeCurrency.value) {
      topUpLimits.value = null
      return
    }

    topUpLimitsLoading.value = true
    try {
      const { data } = await $http.get(
        '/v2/wallet/payment-limits',
        {
          params: {
            transaction_type: 'topup',
            currency_code: activeCurrency.value,
          },
          "headers": {
            "Authorization": "Token " + authToken.value
          },
          validateStatus: () => true
        }
      )
      topUpLimits.value = data?.result || null
    } catch (error) {
      topUpLimits.value = null
    } finally {
      topUpLimitsLoading.value = false
    }
  }

  watch([activeCurrency, authToken], () => {
    fetchTopUpLimits()
  }, { immediate: true })

  watch([routeAccountId, walletList], ([accountId]) => {
    if (route.name !== 'WalletTopUp' || !accountId) {
      return
    }

    const account = walletList.value.find(({ id }) => id === accountId)
    if (account && account.id !== activeWallet.value?.id) {
      vuexStore.commit('updateCurrentWallet', account)
    }
  }, { immediate: true })

  const redirectToPayment = () => {
    if (!canContinueToPayment.value) {
      return
    }

    if (authStore.appType === 'web') {
      window.open(paymentUrl.value, '_blank')
    } else {
      window.location = paymentUrl.value
    }
  }

  const fetchCreatedTransaction = async (walletTransactionId, options = {}) => {
    if (!walletTransactionId || transactionLoading.value) {
      return
    }

    const shouldHoldAnimation = Boolean(options.holdAnimation)
    const animationStartedAt = Date.now()
    transactionLoading.value = true
    transactionRefreshAnimating.value = true

    try {
      const { data } = await $http.get(
        `/v2/wallet/transaction/${walletTransactionId}`,
        {
          "headers": {
            "Authorization": "Token " + authToken.value
          },
          validateStatus: () => true
        }
      )

      if (data?.result) {
        createdTransaction.value = data.result
        if (data.result.payment_url || !paymentUrl.value) {
          paymentUrl.value = data.result.payment_url || ''
        }
        transactionDetailsLoaded.value = true
      } else {
        errorMessage.value = text('wallet_topup.error')
      }
    } catch (error) {
      errorMessage.value = text('wallet_topup.error')
    } finally {
      const elapsedMs = Date.now() - animationStartedAt
      const remainingAnimationMs = shouldHoldAnimation
        ? Math.max(minRefreshAnimationMs - elapsedMs, 0)
        : 0

      window.setTimeout(() => {
        transactionLoading.value = false
        transactionRefreshAnimating.value = false
      }, remainingAnimationMs)
    }
  }

  const refreshTransactionStatus = () => {
    fetchCreatedTransaction(walletTransactionId.value, { holdAnimation: true })
  }

  watch(routeWalletTransactionId, (walletTransactionId) => {
    if (!walletTransactionId) {
      return
    }

    errorMessage.value = ''
    paymentUrl.value = ''
    createdTransaction.value = null
    createdTransactionFallback.value = null
    transactionDetailsLoaded.value = false
    fetchCreatedTransaction(walletTransactionId)
  }, { immediate: true })

  const TopUpFromBankOrCrypto = async ()=> {
    if (!amount.value) {
      errorMessage.value = text('wallet_topup.error_empty_amount')
      return
    }  else {
      errorMessage.value = ''
    }

    loading.value = true

    const { data } = await $http.post(
        '/v2/wallet/top-up',
        {
          "amount": amount.value,
          "currency_code": activeWallet.value.currency_code
        },
        {
          "headers": {
            "Authorization": "Token " + authToken.value
          },
          validateStatus: () => true
        }
    );

    if (data.status === "error") {
      errorMessage.value = data.message
      loading.value = false
    } else {
      paymentUrl.value = data.url || ''
      transactionDetailsLoaded.value = false
      createdTransactionFallback.value = {
        id: data.wallet_transaction_id,
        amount: amount.value,
        amount_paid: null,
        currency_code: activeWallet.value.currency_code,
        status: 'pending',
        wallet_transaction_type: 'topup',
        created_at: new Date().toISOString(),
      }
      loading.value = false
      amount.value = ''

      if (data.wallet_transaction_id) {
        router.replace({
          name: 'WalletTopUpTransaction',
          params: { id: data.wallet_transaction_id },
        })
        fetchCreatedTransaction(data.wallet_transaction_id)
      }
    }
  }
</script>

<template>
  <div class="wallet_topup">
    <div v-if="topUpTransaction" class="topup-transaction" data-testid="top-up-transaction">
      <div class="title">
        <div class="back-btn__wrapper">
          <BackBtn @click="$router.back()"/>
        </div>
        {{ text('wallet_topup.transaction_created') }}
      </div>
      <div class="transaction-status-row">
        <div class="transaction-status" data-testid="payment-status">
          {{ text(`transactions.list.status.${topUpTransaction.status}`) }}
        </div>
        <button
          type="button"
          class="refresh-status-button"
          :class="{ loading: transactionRefreshAnimating }"
          :disabled="transactionLoading || !walletTransactionId"
          :aria-label="text('wallet_topup.refresh_status')"
          :title="text('wallet_topup.refresh_status')"
          @click="refreshTransactionStatus"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M20 12a8 8 0 1 1-2.34-5.66" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            <path d="M20 4v6h-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
      <div class="transaction-summary">
        <div class="summary-row">
          <div class="label">{{ text('transactions.list.details.transaction.type') }}</div>
          <div class="value">{{ text('transactions.list.topup') }}</div>
        </div>
        <div v-if="topUpTransaction.amount !== undefined && topUpTransaction.amount !== null" class="summary-row">
          <div class="label">{{ text('transactions.list.details.transaction.amount') }}</div>
          <div class="value amount">
            {{ parseFloat(topUpTransaction.amount) }} {{ getCurrencySymbol(topUpTransaction.currency_code) }}
          </div>
        </div>
        <div v-if="topUpTransaction.currency_code" class="summary-row">
          <div class="label">{{ text('transactions.list.details.transaction.currency') }}</div>
          <div class="value">{{ topUpTransaction.currency_code }}</div>
        </div>
        <div class="summary-row">
          <div class="label">{{ text('transactions.list.details.transaction.status') }}</div>
          <div class="value">{{ text(`transactions.list.status.${topUpTransaction.status}`) }}</div>
        </div>
        <div v-if="topUpTransaction.created_at" class="summary-row">
          <div class="label">{{ text('transactions.list.details.transaction.date') }}</div>
          <div class="value">{{ formatDateTime(topUpTransaction.created_at) }}</div>
        </div>
        <div v-if="topUpTransaction.id" class="summary-row">
          <div class="label">{{ text('transactions.list.details.transaction.id') }}</div>
          <div class="value transaction-id">
            {{ shortenString(topUpTransaction.id) }}
            <Copy :value="topUpTransaction.id" />
          </div>
        </div>
      </div>
      <div v-if="canContinueToPayment" class="redirect-message">
        {{ text('wallet_topup.redirect_message') }}
      </div>
      <BaseButton
        v-if="canContinueToPayment"
        grayscale-accent
        @click="redirectToPayment"
      >
        <template #label>
          {{ text('wallet_topup.continue_to_payment') }}
        </template>
      </BaseButton>
      <div v-else-if="showRedirectUnavailable" class="redirect-unavailable">
        {{ text('wallet_topup.redirect_unavailable') }}
      </div>
      <div v-if="errorMessage" class="error">{{ errorMessage }}</div>
      <div v-if="transactionLoading" class="transaction-loading">
        {{ text('wallet_topup.transaction_loading') }}
      </div>
    </div>
    <template v-else>
    <div>
      <div class="title">
        <div class="back-btn__wrapper">
          <BackBtn @click="$router.back()"/>
        </div>
        {{ text('wallet_topup.select_account') }}
      </div>
      <WalletList style="margin-bottom: 32px" />
      <BaseInput
        v-model="amount"
        :label="text('wallet_withdrawal.amount')"
        :placeholder="text('wallet_withdrawal.amount')"
        number
      />
      <div v-if="visibleTopUpLimits" class="limits-summary">
        Limits: {{ visibleTopUpLimits }}
      </div>
    </div>
    <div v-if="errorMessage" class="error">{{ errorMessage }}</div>

    <div>
      <div class="actions-block">
        <BaseButton
          grayscale-accent
          :disabled="topUpDisabled"
          @click="TopUpFromBankOrCrypto"
        >
          <template #label>
            {{ text('wallet_topup.top_up') }}
          </template>
        </BaseButton>
        <BaseButton
          v-if="envName === 'stater'"
          grayscale-accent prefix
          :disabled="topUpDisabled"
          @click="cashModalActive = true"
        >
          <template #prefix>
            <img height="24" src="@/assets/images/cash.svg" alt="sbp" />
          </template>
          <template #label>
            {{ text('wallet_topup.methods.cash') }}
          </template>
        </BaseButton>
      </div>
      <TextModal
          @closeModal="cashModalActive = false"
          v-if="cashModalActive"
          :title="text('wallet_topup.modal.title')"
          :text="cashModalText"
      />
    </div>

    <div
        v-if="!termsModel && dynamicTermsLink"
        class="terms_checkbox"
    >
      <label class="checkbox_container">
        <input type="checkbox" v-model="termsModel" />
        <span>{{ text('wallet_topup.accept_the') }}</span>
        <a :href="dynamicTermsLink" target="_blank">{{ text('wallet_topup.terms_of_use') }}</a>
      </label>
    </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
  .wallet_topup {
    padding: 24px;
    position: relative;
    height: 100%;
    min-height: 600px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 16px;

    .title {
      position: relative;

      color: var(--text-color-primary);
      font-family: "Rubik", sans-serif;
      font-size: 21px;
      font-style: normal;
      font-weight: 700;
      text-align: center;
      margin-bottom: 32px;

      .back-btn__wrapper {
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
      }
    }

    ::v-deep(.base-input) {
      background-color: var(--layout-bg)!important;

      //background-color: var(--layout-bg)!important;

      &.blur {
        padding-top: 30px!important;
      }
    }

    .error {
      color: var(--text-color-error);
      text-align: center;
      font-family: "Rubik", sans-serif;
      font-size: 16px;
      font-style: normal;
      margin: 16px 0;
    }

    .limits-summary {
      color: var(--text-color-secondary);
      font-family: "Rubik", sans-serif;
      font-size: 13px;
      line-height: 135%;
      margin-top: 8px;
      text-align: center;
    }

    .actions-block {
      width: 100%;
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
      margin-top: 10px;
      .base-button {
        box-sizing: border-box;
        width: 100%;
      }

      &:has(.base-button:nth-of-type(2)) .base-button {
        width: calc(50% - 8px);
      }
    }

    .checkbox_container {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 24px 0;
    }

    .checkbox_container input[type="checkbox"] {
      margin: 0;
    }

    .payment_security_section {
      background-color: white;
      padding: 16px;
      margin: 0 24px 0 24px;
      border-radius: 12px;
    }

    .payment_methods {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      opacity: 0.8;
    }

    .payment_methods img {
      flex: 1;
      max-width: 35%;
      height: auto;
    }

    .topup-transaction {
      display: flex;
      flex-direction: column;
      gap: 20px;
      height: 100%;
    }

    .transaction-status-row {
      align-self: center;
      display: grid;
      grid-template-columns: 32px auto 32px;
      align-items: center;
      gap: 8px;
    }

    .transaction-status {
      grid-column: 2;
      border-radius: 999px;
      background: var(--accent-bg);
      color: var(--text-color-primary-reverse);
      font-family: "Rubik", sans-serif;
      font-size: 14px;
      font-weight: 600;
      padding: 8px 14px;
    }

    .refresh-status-button {
      grid-column: 3;
      width: 32px;
      height: 32px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: 1px solid var(--border-color);
      border-radius: 50%;
      background: var(--element-bg);
      color: var(--text-color-secondary);
      cursor: pointer;
      transition: 0.2s ease all;

      &:hover:not(:disabled) {
        background: var(--element-bg-lighter);
        color: var(--text-color-primary);
      }

      &:disabled {
        cursor: default;
        opacity: 0.55;
      }

      &.loading svg {
        animation: refresh-status-spin 0.8s linear infinite;
      }
    }

    @keyframes refresh-status-spin {
      from {
        transform: rotate(0deg);
      }

      to {
        transform: rotate(360deg);
      }
    }

    .transaction-summary {
      display: flex;
      flex-direction: column;
      gap: 12px;
      border-radius: 8px;
      background: var(--element-bg);
      padding: 16px;
    }

    .summary-row {
      display: flex;
      justify-content: space-between;
      gap: 16px;
      color: var(--text-color-primary);
      font-family: "Rubik", sans-serif;
      font-size: 14px;
      line-height: 135%;

      .label {
        color: var(--text-color-secondary);
      }

      .value {
        display: flex;
        align-items: center;
        gap: 6px;
        min-width: 0;
        text-align: right;
        word-break: break-word;
      }

      .amount {
        font-weight: 600;
      }
    }

    .redirect-message,
    .transaction-loading {
      color: var(--text-color-secondary);
      font-family: "Rubik", sans-serif;
      font-size: 14px;
      line-height: 135%;
      text-align: center;
    }

    .redirect-unavailable {
      border-radius: 8px;
      background: var(--error-bg);
      color: var(--text-color-error);
      font-family: "Rubik", sans-serif;
      font-size: 14px;
      line-height: 135%;
      padding: 12px;
      text-align: center;
    }

    .transaction-id {
      justify-content: flex-end;
    }

    .checkbox_container {
      input {
        cursor: pointer;
      }
    }

    .bank-card-btn-prefix {
      display: flex;
      align-items: center;
      gap: 8px;
      padding-right: 5px;

      img {
        height: 16px;
      }
    }

    @media (max-width: 920px) {
      padding: 16px;
    }
  }
</style>
